import { useState } from 'react';
import type { CompletedStep } from '../types';
import type { ReactNode } from 'react';
import { STEP_LABELS } from './constants';
import { STEP_ICONS } from './icons';
import { SelectedTemplatesList } from './SelectedTemplatesList';
import { TemplateInstantiationDetails } from './TemplateInstantiationDetails';
import { ToolCallDetails } from './ToolCallDetails';
import { ValidationErrorList } from './ValidationErrorList';
import { RunTestsResultsList } from './RunTestsResultsList';
import { TestWritesList } from './TestWritesList';

export function PipelineStepRow({ cs }: { cs: CompletedStep }) {
  // Default-expand failing-test rows so the user immediately sees what
  // broke without an extra click. Every other row stays collapsed —
  // matching the existing behaviour for tool errors, validation issues,
  // and the rest of the chat trail.
  const defaultExpanded = cs.failed === true && !!cs.testResults;
  const [expanded, setExpanded] = useState(defaultExpanded);

  let icon: ReactNode;
  let iconClass = 'status-check';
  if (cs.failed === true) {
    icon = '✗';
    iconClass = 'status-check failed';
  } else if (cs.failed === false) {
    icon = '✓';
  } else {
    icon = STEP_ICONS[cs.step] || '✓';
  }
  const rawMessage = cs.message || STEP_LABELS[cs.step] || cs.step;
  const displayMessage = rawMessage.replace(/^[✓✗]\s+/, '');
  const duration = cs.detail && typeof cs.detail === 'string' ? undefined : undefined;

  const hasToolDetails = !!(cs.toolInput || cs.toolResult || cs.fileContentBefore);
  const hasSelectedTemplates = !!(cs.selectedTemplates && cs.selectedTemplates.length > 0);
  const isTemplateInstantiation = cs.tool === 'generateFromTemplate' && hasToolDetails;
  const hasValidationErrors = !!(cs.validationErrors && cs.validationErrors.length > 0);
  const hasTestResults = !!cs.testResults;
  const hasTestWrites = !!(cs.testWrites && cs.testWrites.length > 0);
  // Failed tool entries are always expandable — even if `toolInput`/`toolResult`
  // happen to be missing on the wire, we still show whatever we have (at minimum
  // the tool name + raw message). Catches the case where the user sees "× X failed"
  // and has no way to drill into what happened.
  const isFailedTool = cs.failed === true && !!cs.tool;
  const isClickable =
    hasToolDetails ||
    isFailedTool ||
    (cs.actions && cs.actions.length > 0) ||
    hasSelectedTemplates ||
    hasValidationErrors ||
    hasTestResults ||
    hasTestWrites;

  return (
    <div className="pipeline-step-group" data-testid="pipeline-completed-step">
      <div
        className={`pipeline-status-row completed${cs.failed ? ' failed' : ''}${isClickable ? ' clickable' : ''}`}
        onClick={isClickable ? () => setExpanded(!expanded) : undefined}
        style={isClickable ? { cursor: 'pointer' } : undefined}
      >
        {isClickable && <span className="step-expand-icon">{expanded ? '▾' : '▸'}</span>}
        <span className={iconClass}>{icon}</span>
        <span className="pipeline-step-label completed">{displayMessage}</span>
        {cs.tool && <span className="pipeline-tool-badge">{cs.tool}</span>}
        {hasSelectedTemplates && (
          <span className="pipeline-step-detail" data-testid="pipeline-selected-templates-count">
            {cs.selectedTemplates!.length} template{cs.selectedTemplates!.length !== 1 ? 's' : ''}
          </span>
        )}
        {hasTestWrites && (
          <span className="pipeline-step-detail" data-testid="pipeline-test-writes-count">
            {cs.testWrites!.length} spec file{cs.testWrites!.length !== 1 ? 's' : ''}
          </span>
        )}
        {!hasSelectedTemplates && cs.detail && !cs.actions && (
          <span className="pipeline-step-detail" data-testid="pipeline-step-detail">{cs.detail}</span>
        )}
        {duration && (
          <span className="pipeline-step-duration">{duration}</span>
        )}
      </div>
      {expanded && cs.actions && cs.actions.length > 0 && (
        <div className="pipeline-plan-actions">
          {cs.reasoning && (
            <div className="pipeline-plan-reasoning">{cs.reasoning}</div>
          )}
          {cs.actions.map((action, i) => (
            <div key={i} className="pipeline-plan-action">
              <span className="plan-action-num">{i + 1}.</span>
              <span className="pipeline-tool-badge">{action.tool}</span>
              {action.reason && <span className="plan-action-reason">{action.reason}</span>}
            </div>
          ))}
        </div>
      )}
      {expanded && hasSelectedTemplates && (
        <SelectedTemplatesList templates={cs.selectedTemplates!} />
      )}
      {expanded && isTemplateInstantiation && (
        <TemplateInstantiationDetails
          toolInput={cs.toolInput}
          toolResult={cs.toolResult}
        />
      )}
      {expanded && (hasToolDetails || isFailedTool) && !isTemplateInstantiation && (
        <ToolCallDetails
          toolInput={cs.toolInput}
          toolResult={cs.toolResult}
          fileContentBefore={cs.fileContentBefore}
          fallbackErrorMessage={
            isFailedTool && !cs.toolResult
              ? rawMessage.replace(/^[✗×]\s+/, '')
              : undefined
          }
        />
      )}
      {expanded && hasValidationErrors && (
        <ValidationErrorList errors={cs.validationErrors!} />
      )}
      {expanded && hasTestResults && (
        <RunTestsResultsList results={cs.testResults!} />
      )}
      {expanded && hasTestWrites && (
        <TestWritesList writes={cs.testWrites!} />
      )}
    </div>
  );
}
